import { cloneDeep } from 'lodash';
import {
  AccountClient,
  apiResolver,
  GET_CUSTOM_FIELD_BY_NAME,
  GET_CLIENTS,
  GET_REQUESTERS_BY_CRITERIA_ASSOCIATIONS,
  TicketClient,
  RMMClient
} from '@nile/graphql';
import { GET_CLIENT_SITE_LIST_FOR_DROPDOWN } from '@nile/banyan/src/Graphql/Mutations/site';
import { ALL_MODULES } from '@nile/components';
import {
  GET_WORKSTATION_LIST,
  GET_BIT_LOCKER_STATUSES,
  GET_PATCH_STATUSES,
  GET_ASSET_GROUP_V2
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { isEmpty } from 'lodash';
import { platformCategoryOptions } from '@nile/rmm/src/modules/asset-management/components/constants/asset-rule-builder';

let sourceConfig = {};

const setConfig = (config) => {
  sourceConfig = config;
};

const getConfig = () => {
  return sourceConfig;
};

const getDefaultValue = (f, condition) => {
  const operands = cloneDeep(condition.operands);
  const ruleIdx = operands.findIndex((el) => {
    if (f.dataType === 'entity') {
      return el.attribute === `${f.name}.name`;
    }
    return el.attribute === f.name;
  });
  if (ruleIdx > -1) {
    const rule = operands[ruleIdx];
    switch (f.dataType) {
      case 'date':
      case 'datetime':
        return rule.value;
      default:
        break;
    }
    switch (f.filterType) {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return rule.value;
      default:
        return rule.value.map((el) => {
          return {
            name: el,
            value: el,
            label: el
          };
        });
    }
  }
  return '';
};

/**
 * @description This method gets triggered for custom field or any other field for which we want the query to be dynamic (refer checkDynamicQuery function)
 * Calls the respective API based on isCustom/attribute name
 * @param {object} - f
 * @return {object} - query
 */
const getDynamicQuery = (f) => {
  const resolverModule = 'Ticket';
  let client = TicketClient;
  let query = apiResolver[resolverModule][f.name]?.api;

  switch (f.name) {
    case 'client.name':
      client = AccountClient;
      query = GET_CLIENTS;
      return client.query({
        query: query,
        variables: {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        }
      });
    case 'owner.name':
      const defaultCondition = getConfig()?.asset;
      client = AccountClient;
      query = GET_REQUESTERS_BY_CRITERIA_ASSOCIATIONS;
      return client.query({
        query: query,
        variables: {
          input: {
            listInfo: !isEmpty(defaultCondition)
              ? {
                condition: {
                  joinOperator: 'AND',
                  operands: [defaultCondition]
                }
              }
              : {}
          }
        }
      });
    case 'site.name':
      // We need to filter sites by client name by default in client details -> assets (this condition will only be applied in client details -> assets)
      let siteCondition = getConfig()?.site;
      return AccountClient.query({
        query: GET_CLIENT_SITE_LIST_FOR_DROPDOWN,
        variables: !isEmpty(siteCondition)
          ? {
            // Deprecation special handling: TODO: Special
            input: {
              condition: {
                joinOperator: 'AND',
                operands: [siteCondition]
              },
              sort: {
                attribute: 'name',
                order: 'ASC'
              }
            }
          }
          : {
            input: {
              sort: {
                attribute: 'name',
                order: 'ASC'
              }
            }
          }
      });
    case 'platformCategory':
      return {
        data: platformCategoryOptions
      };
    case 'status':
      return {
        data: [
          {
            label: 'ONLINE',
            value: 'ONLINE'
          },
          {
            label: 'OFFLINE',
            value: 'OFFLINE'
          }
        ]
      };
    case 'bitLockerStatus':
      return RMMClient.query({
        query: GET_BIT_LOCKER_STATUSES
      });
    case 'patchStatus':
      return RMMClient.query({
        query: GET_PATCH_STATUSES
      });
    case 'enrollmentStatus':
      return {
        data: [
          {
            name: 'Yet to Enroll',
            label: 'Yet to Enroll',
            value: 'UN_ENROLLED'
          },
          {
            name: 'Ready for Enrollment',
            label: 'Ready for Enrollment',
            value: 'READY_FOR_ENROLLMENT'
          },
          { name: 'Enrolled', label: 'Enrolled', value: 'ENROLLED' }
          // { label: 'Enrolled', value: 'ENROLLED' }
        ]
      };
    default:
      if (f.isCustom) {
        return client.query({
          query: GET_CUSTOM_FIELD_BY_NAME,
          variables: {
            columnName: f.name,
            module: ALL_MODULES.asset,
            assetClassEntityName: 'WorkStation'
          }
        });
      } else {
        query = apiResolver[resolverModule][f.name]?.api;
        client = apiResolver[resolverModule][f.name]?.client;
        return client.query({
          query: query
        });
      }
  }
};

/**
 * @description This method check whether a particular attribute requires the query to be dynamic or not
 * @param {object} - f
 * @return {boolean}
 * @public
 */
const checkDynamicQuery = (f) => {
  if (f.isCustom) return true;
  switch (f.name) {
    case 'status':
    case 'platformCategory':
    case 'site.name':
    case 'client.name':
    case 'bitLockerStatus':
    case 'patchStatus':
    case 'owner.name':
    case 'enrollmentStatus':
      return true;
    default:
      return false;
  }
};

/**
 * @description This method is responsible to format the data according to the react-select for the dynamic query attributes
 * @param {object} - f
 * @param {Array<object>} - data
 * @return {Array | Array<object>}
 * @public
 */
const generateOptions = (f, data) => {
  let allValues = [];
  switch (f.name) {
    case 'client.name':
      allValues = data.getClientList.clients.map((el) => {
        return {
          ...el,
          name: el.accountId,
          value: el.name,
          label: el.name
        };
      });
      break;
    case 'owner.name':
      allValues = data?.getClientUserAssociationsList?.associations?.map(
        (el) => {
          return {
            ...el,
            name: el?.user?.userId,
            value: el?.user.name,
            label: el?.user.name
          };
        }
      );
      break;
    case 'site.name':
      allValues = data.getClientSiteList.sites.map((el) => {
        return {
          ...el,
          name: el.accountId,
          value: el.name,
          label: el.name
        };
      });
      break;
    case 'status':
    case 'platformCategory':
      allValues = allValues.concat(data);
      break;
    case 'bitLockerStatus':
      allValues = data?.getAssetBitLockerStatuses?.map((el) => {
        return {
          name: el,
          value: el,
          label: el
        };
      });
      break;
    case 'patchStatus':
      allValues = data?.getAssetPatchStatusMetaInfo?.map((el) => {
        return {
          name: el,
          value: el,
          label: el
        };
      });
      break;
    case 'department.name':
      allValues = data?.getBusinessFunctionList?.map((el) => {
        return {
          name: el?.businessFunctionId,
          value: el?.name,
          label: el?.name
        };
      });
      break;
    case 'enrollmentStatus':
      return [
        { name: 'UN_ENROLLED', label: 'UN_ENROLLED', value: 'UN_ENROLLED' },
        {
          name: 'READY_FOR_ENROLLMENT',
          label: 'READY_FOR_ENROLLMENT',
          value: 'READY_FOR_ENROLLMENT'
        },
        { name: 'ENROLLED', label: 'ENROLLED', value: 'ENROLLED' }
      ];
    default:
      if (f.isCustom) {
        allValues = data.getCustomFieldByName.options.map((el) => {
          return {
            name: el,
            value: el,
            label: el
          };
        });
        return allValues;
      } else {
        allValues = [];
      }
  }
  return allValues;
};

/**
 * @description There are few attributes for which we don't want to fetch the values from the server on selecting the attribute
 * rather, we want to fetch on typing atleast 2 character, that is when this function gets triggered (in the attribute object, we have 'autocomplete' property as true)
 * @param {object} - client
 * @param {object} - query
 * @param {object} - params
 * @return {Array | Array<object>}
 * @public
 */
const fetchValues = async (client, query, params) => {
  const attribute = 'name';
  const nameKey = getNameKey(params.property);

  let variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: attribute,
            operator: 'contains',
            value: params.inputValue
          }
        ]
      }
    }
  };

  // We need to filter assets by client name by default in client details -> assets (this condition will only be applied in client details -> assets)
  let assetCondition = getConfig()?.asset;
  let viewCondition = getConfig()?.viewId;

  if (!isEmpty(assetCondition) && nameKey === 'getWorkStationList') {
    variables.listInfo.condition.operands.push(assetCondition);
  }

  if (
    !isEmpty(viewCondition) &&
    (nameKey === 'getWorkStationList' || nameKey === 'getAssetGroupAssetsV2')
  ) {
    variables = {
      listInfo: {
        ...variables.listInfo,
        view: {
          id: viewCondition
        }
      }
    };
  }

  const res = await client.query({
    query: query,
    variables
  });

  let allOptions = [];

  if (nameKey === 'getWorkStationList') {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey].assets.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
      : [];
  } else if (nameKey === 'getAssetGroupAssetsV2') {
    allOptions = res?.data?.[nameKey]
      ? res.data.assets.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
      : [];
  } else if (params?.property?.name === 'owner.name') {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey]?.userList?.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
      : [];
  } else {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey].map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
      : [];
  }

  return allOptions;
};

/**
 * @description There are few attributes for which we don't want to fetch the values from the server on selecting the attribute
 * rather, we want to fetch on typing atleast 2 character, that is when this function gets triggered (in the attribute object, we have 'autocomplete' property as true)
 * @param {object} - client
 * @param {object} - query
 * @param {object} - params
 * @return {Array | Array<object>}
 * @public
 */
const fetchValuesGroupValues = async (client, query, params) => {
  const attribute = 'name';
  const nameKey = getNameKey(params.property, params.property.name === 'name');

  let variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: attribute,
            operator: 'contains',
            value: params.inputValue
          }
        ]
      }
    }
  };

  // We need to filter assets by client name by default in client details -> assets (this condition will only be applied in client details -> assets)
  let assetCondition = getConfig()?.asset;
  let viewCondition = getConfig()?.viewId;
  let groupCondition = getConfig()?.groupId;

  if (!isEmpty(assetCondition) && nameKey === 'getAssetGroupAssetsV2') {
    variables.listInfo.condition.operands.push(assetCondition);
  }

  if (!isEmpty(groupCondition) && nameKey === 'getAssetGroupAssetsV2') {
    variables = {
      listInfo: variables.listInfo,
      groupId: groupCondition
    };
  }

  const res = await client.query({
    query: query,
    variables
  });

  let allOptions = [];

  if (nameKey === 'getWorkStationList') {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey].assets.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
      : [];
  } else if (nameKey === 'getAssetGroupAssetsV2') {
    allOptions = res?.data?.[nameKey]
      ? res?.data?.[nameKey].assets.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
      : [];
  } else if (params?.property?.name === 'owner.name') {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey]?.userList?.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
      : [];
  } else {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey].map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
      : [];
  }

  return allOptions;
};

/**
 * @description returns the graphql schema based on the attribute
 * @param {object} - property
 * @return {object}
 * @private
 */
function getQuery(property, isAssetGroup = false) {
  if (isAssetGroup) {
    return GET_ASSET_GROUP_V2;
  }
  switch (property.name) {
    case 'name':
      return GET_WORKSTATION_LIST;
    default:
      break;
  }
}

/**
 * @description returns the accessKey based on the attribute
 * @param {object} - property
 * @return {string}
 * @private
 */
function getNameKey(property, isAssetGroup = false) {
  if (isAssetGroup) {
    return 'getAssetGroupAssetsV2';
  }
  switch (property.name) {
    case 'name':
    case 'workStation.name':
      return 'getWorkStationList';
    default:
      return 'getRequesterList';
  }
}

/**
 * @description returns RMMClient and query based on the selected attribute
 * @param {object} - property
 * @return {object}
 * @public
 */
const getAsyncOptions = (property) => {
  switch (property.name) {
    case 'owner.name':
      return {
        client: AccountClient,
        query: GET_REQUESTERS_BY_CRITERIA_ASSOCIATIONS
      };
    default:
      return { client: RMMClient, query: getQuery(property) };
  }
};

/**
 * @description returns RMMClient and query based on the selected attribute
 * @param {object} - property
 * @return {object}
 * @public
 */
const getGroupAsyncOptions = (property) => {
  switch (property.name) {
    case 'owner.name':
      return {
        client: AccountClient,
        query: GET_REQUESTERS_BY_CRITERIA_ASSOCIATIONS
      };
    default:
      return {
        client: RMMClient,
        query: getQuery(property, property.name === 'name')
      };
  }
};

const getVariables = (property) => {
  let baseVariables = {};
  if (!isEmpty(getConfig())) {
    baseVariables = getConfig();
  }
  switch (property.name) {
    case 'site.name':
    case 'asset.name':
    case 'owner.name':
      return { listInfo: { ...baseVariables } };
    default:
      return null;
  }
};

export {
  getDefaultValue,
  getDynamicQuery,
  checkDynamicQuery,
  generateOptions,
  fetchValues,
  getAsyncOptions,
  getVariables,
  setConfig,
  getConfig,
  getGroupAsyncOptions,
  fetchValuesGroupValues
};
